import React, { useEffect, useMemo, useState } from 'react';
import { Plus, X, ArrowLeftRight, ArrowLeft, Inbox, Code2, ChevronDown, ChevronRight, Trash2, Upload, Download, FlaskConical, RotateCcw, AlertTriangle, FolderOpen, KeyRound, RefreshCw, SlidersHorizontal, Play } from 'lucide-react';
import ApiKeyField, { generateApiKey } from './shared/ApiKeyField';
import CredentialSelector, { joinOverrides, splitOverrides } from './shared/CredentialSelector';
import { CredentialModalById } from './CredentialsTab';
import CopyButton from './shared/CopyButton';
import AddRouteModal from './shared/AddRouteModal';
import { routeLabel } from './shared/routeName';
import ContactBadge from './shared/ContactBadge';
import './IntegrationDesigner.css';

// The board an integration is drawn on.
//
//   ┌ System ┐   ┌ Proxy ──────────────┐        ┌ Universal API ───┐   ┌ Targets ──────┐
//   ┆        ┆   │ API KEY  ……         │        │                  │   │               │
//   ┆ OPERA  ┆   │ ┌ /rsv, /bof ─────┐ │ ◀────▶ │ Hilton UAPI      │◀─▶│ Oracle OHIP   │
//   ┆ OXI    ┆◀─▶│ │ Custom Code     │ │        │                  │   │               │
//   ┆        ┆   │ ├ /crm ───────────┤ │ ◀────▶ │ Other credential │◀─▶│ Azure BLOB    │
//   ┆        ┆   │ │ Custom Code     │ │        │                  │   │               │
//   └┄┄┄┄┄┄┄┄┘   └─────────────────────┘        └──────────────────┘   └───────────────┘
//
// ONE BOARD PER PROXY, AND A ROUTE IS A ROW ACROSS IT. Three columns — the proxy, the Universal API, the
// targets — and a route occupies one row of all three: what it carries on the left, the credential it is
// made with in the middle, where that lands on the right. Drawn as a list of lanes under a separate
// picture, the reader had to hold that mapping in their head; here the row IS the mapping.
//
// A GRID, NOT THREE STACKED COLUMNS. Stacked columns line up only while their boxes happen to be the same
// height. A grid row cannot come apart.
//
// THE CREDENTIAL IS THE DESTINATION (V479). Which backend a route reaches is decided by the Universal API
// credential it is made with, so the right-hand column is read from the credential rather than chosen
// again. A target box is drawn only when somebody wants the picture to say so explicitly.

const HUB_LABEL = 'Universal API';

// The key a route's grants are looked up by: its credential AND the backend binding it lands on, because a
// binding with override_modules = 'Y' answers for itself (/opt/core/uapi-entitlement). Shared with
// IntegrationForm, which fetches them, so the two cannot key them differently.
export const routeGrantKey = (route) => `${route.credential_id || ''}:${route.uapi_access_id || ''}`;

// Where the grants came from, in words, for the tooltip.
const GRANT_SOURCE = {
    access: 'set on this backend binding (it overrides the credential)',
    profile: 'from the access profile',
    credential: 'on the credential itself',
};

// What a box says, in order of what the reader most needs: an override, then the system's name, then
// something that at least identifies the row.
const nodeTitle = (node) => node.label || node.vendor_name || node.system_name || `System #${node.marketplace_id || '?'}`;

const IntegrationDesigner = ({
    nodes,
    systems,
    readOnly = false,
    // WHY THE BOARD CANNOT BE EDITED — said in the caller's own terms. A control disabled for two
    // different reasons that only ever gives one of them is worse than one that says nothing: on an
    // implementation's board the hotel IS chosen, so "choose a hotel in the top bar" reads as a fault
    // to go and fix, and there is nothing to fix.
    readOnlyReason = 'This board is read-only here.',
    // THE SHAPE AND THE CREDENTIALS LOCK SEPARATELY (V508).
    //
    // An implementation's board is the template's shape — which is not editable here — filled with one
    // property's credentials, which ARE. Forcing that through the wizard meant the board showed exactly
    // the empty slot somebody wanted to fill and refused to let them fill it, with the fix three clicks
    // away on the same screen.
    //
    // Defaults to `readOnly`, so every existing caller behaves as before.
    credentialsReadOnly = readOnly,
    // ONE UNIVERSAL API CREDENTIAL FOR THE WHOLE IMPLEMENTATION (V649) — { id, name, onChange(id) }, from an
    // implementation's board. Every route is made as it, so it is set once, above the first Universal API box
    // (a chevron, superadmin only), and each route's cell shows only what differs between routes: the x-hotelid
    // of the binding it lands through. Absent (the template designer, a log), each route draws its own as before.
    uapiCredential = null,
    // (route, credential) => void — the credential a route's TARGET signs in with, picked from the target's own
    // Credential button in the shared credential picker (an implementation's board, superadmin). The caller binds
    // it — updating the route's binding, or creating one and with it the route's x-hotelid. Absent, the button only
    // shows the bound credential.
    onPickTargetCredential,
    // (route, patch) => void — how a route's Universal API credential and backend binding are changed.
    // Absent, those cells stay as text, which is what the template designer wants: on a template a
    // route HAS no credential.
    onUpdateRoute,
    // (route) => void — open the queue for a target that polls. ONLY AN IMPLEMENTATION HAS ONE: a
    // template has no property and no binding, so there is nothing queued against it and the button
    // would open an empty box that means nothing. Absent on the design board, for that reason.
    onViewQueue,
    onViewFiles,
    onAdd,            // (role) => void — opens the caller's system picker
    // A VENDOR'S BOARD (V704). vendors = the owning partner's Universal API vendors { id, name, system_name, system_image }
    // (from the graph); onAddVendor(vendor) puts one on the design. Absent, there is no Add vendor button.
    vendors = [],
    onAddVendor,
    onRemove,         // (nodeId) => void
    onUpdateNode,     // (nodeId, patch) => void — edits a node in place (the proxy's API key)
    routes = [],      // the rows: { id, from_node_id, to_node_id, credential_id, modules[], active }
    onRoutesChange,   // (routes) => void
    credentials = [], // the hotel's Universal API credentials: { id, credential_name }
    access = [],      // the property's backend bindings (uapi_hotel_access, V419):
                      // { id, marketplace_id, system_name, credential_id, credential_name, uapi_hotel_id }
    routeGrants = {},       // { [routeGrantKey(route)]: { source, profileName, modules: [{ uapi_route_id, path }] } }
                            // — what each route's caller may ACTUALLY reach, as the gateway decides it
    hotelId, accountId, partnerId, // the scope a proxy's own credential is picked from and shared to
    isSuperAdmin = false,
    customCode = [],  // snippets, so a row can say how many run on it
    onEditCode,       // (route) => void — opens the custom code editor
    // (route) => void — opens the Universal API custom code the TEMPLATE runs around the route's integration
    // (V533). Absent, the Integration column has no button: an implementation's board shows that code but
    // does not change the template's shape, and a log reads what happened.
    onEditIntegrationCode,
    // (node) => void — opens the test suite for a source. OPTIONAL, and the template designer does not
    // pass it: a test is made as a property, with that property's credential, and a template has none.
    // The button is on each Implementations row instead. Kept as a prop because the board is also
    // rendered read-only elsewhere, where a caller that does have a property can hand one in.
    onTestSource,
    // (node) => void — Events (V686), for a source whose system's events the hub polls (marketplace.uses_event_poller,
    // V635 — the OHIP Adapter) or streams (uses_streaming_client, V616): how they reach the hub, POLL or STREAM, never
    // both. OPTIONAL: the TEMPLATE passes it — set there for every hotel implementing it.
    onEventDelivery,
    // (node) => void — Config, for a source whose system has settings in the PIPELINES sub-group (V654 — the Crawler's
    // url and interval): the values this implementation runs the source with. Implementation only.
    onSourceConfig,
    // (node) => void — Run now, for a source whose system the hub runs on a schedule and can start by hand
    // (marketplace.run_now_button, V658 — the Crawler: one crawl). Implementation only.
    onSourceRunNow,
    // (node) => void — Credentials, for a source whose system has AUTH settings (marketplacesettings.TYPE = 'AUTH'):
    // pick the credential this implementation's adapter signs in to its system with. Implementation only.
    onSourceCredential,

    // ONE MESSAGE, PAINTED ONTO THE DESIGN (the Logs screen).
    //
    // A log is the story of one message travelling through THIS picture, so it is shown ON this picture
    // rather than in a second drawing of the same thing: the boxes go green where the message succeeded,
    // grey where it was bypassed, yellow where it got through with a caveat (a linked record left off) and red
    // where it failed, and clicking one lists what happened there.
    //
    // run = { stateOf(kind, id) -> 'ok' | 'warning' | 'skipped' | 'error' | null, countOf(kind, id) -> number,
    //         onOpen(kind, id) } with kind: 'system' | 'proxy' | 'route' | 'hub' | 'target'.
    run = null,
}) => {
    // SOURCES ONLY. The hub is every integration's middle and is drawn as a column rather than as a box to
    // find, and the targets column is derived from each route's credential (V479) — so neither is looked up
    // here any more. A TARGET node saved by an older design is still honoured: a route that names one shows
    // that name instead of the credential's backend.
    // A VENDOR IS A BOARD TOO (V704) — in the same column as the sources, with no system in front of it.
    const sources = useMemo(() => nodes.filter(n => n.role === 'SOURCE' || n.role === 'VENDOR'), [nodes]);
    const [vendorPick, setVendorPick] = useState(false);
    const vendorsToAdd = useMemo(() => (vendors || []).filter(v =>
        !nodes.some(n => n.role === 'VENDOR' && String(n.vendor_credential_id) === String(v.id))), [vendors, nodes]);

    // Which systems may be offered as a source. The flag on the marketplace record (V468) is the decision;
    // this screen only reads it, and only to tell an empty board apart from an empty marketplace.
    const sourceSystemCount = useMemo(
        () => (systems || []).filter(s => s.is_integration_source === 'Y').length, [systems]);

    // The palette row for a node's system: how a proxy of that kind authenticates, and the modules it deals
    // in. Both are facts about the system, read from its marketplace record.
    const systemById = useMemo(
        () => new Map((systems || []).map(s => [String(s.id), s])), [systems]);

    // Which proxy the Add-route modal was opened from. One at a time: the modal is the whole screen's
    // focus, and two would be a stack nobody asked for.
    const [routeModalNodeId, setRouteModalNodeId] = useState(null);
    // Which proxies have their key and inbound URL SHOWN, by node id. Closed by default: they are set up
    // once and then take a third of the column for the rest of the integration's life, where the routes
    // beneath them are what the screen is read for. The chevron on the proxy's name opens them.
    const [expanded, setExpanded] = useState(() => new Set());
    // The implementation's Universal API credential panel, above the first Universal API box. Closed by default.
    const [uapiCredOpen, setUapiCredOpen] = useState(false);
    // The route whose target credential is being picked, or null: { route, marketplaceId, systemName, selected, values }.
    const [targetPick, setTargetPick] = useState(null);
    // The level a binding's backend credential is owned at, from its owning-level columns.
    const bindingLevel = (b) => (b.credential_partner_id ? 'partner' : b.credential_account_id ? 'account' : 'hotel');

    // OPEN A CREDENTIAL AND SHOW IT — the Source's (what the proxy reaches its own system with), the Universal API's
    // (what the route is made as) and the Integration's (what the binding signs in to the backend with). All three open
    // the shared credential editor (CredentialsTab's CredentialModal, by id), the one the partner / chain / hotel
    // Credentials tabs use; which credential a route uses is still chosen with its dropdown.
    const [credView, setCredView] = useState(null);   // { credentialId, overrides, overridesContext }
    // A binding's or node's per-property values (credential_overrides, V483 — a JSON object or its string) plus its hotel
    // id override, as { SETTINGNAME: value } — what the shared editor shows beside the credential's own values.
    const overridesOf = (o, hotelidOverride) => {
        const parsed = typeof o === 'string' ? (() => { try { return JSON.parse(o); } catch (e) { return {}; } })() : (o || {});
        return { ...parsed, ...(hotelidOverride ? { HOTELID: hotelidOverride } : {}) };
    };
    // A source whose system signs in with a credential (AUTH settings on its marketplace record) — the adapter offers
    // Credentials on an implementation. From the saved node, or the system record for a node added in the browser.
    const hasAuth = (node) => {
        if (run || !onSourceCredential) return false;
        const system = systemById.get(String(node.marketplace_id));
        return Number(node.auth_settings || (system && system.auth_settings) || 0) > 0;
    };
    // A source whose system's events the hub polls or streams (V635 / V616) — the adapter offers Events (V686).
    const deliversEvents = (node) => {
        const system = systemById.get(String(node.marketplace_id));
        const flag = (k) => (node[k] || (system && system[k])) === 'Y';
        return flag('uses_event_poller') || flag('uses_streaming_client');
    };
    // A CREDENTIAL THE IMPLEMENTATION STILL NEEDS IS RED, ONE THAT IS SET IS GREEN — on every button that sets one (the
    // source's, the Universal API's, each target's). The board IS where an implementation is filled in; there is no wizard.
    const credState = (isSet) => (isSet ? ' idz-cred-set' : ' idz-cred-missing');
    const credButton = (credentialId, role, { title, overrides, overridesContext } = {}) => (credentialId ? (
        <button
            type="button"
            className="idz-rt-cred-link idz-cred-view"
            onClick={(e) => { e.stopPropagation(); setCredView({ credentialId, overrides, overridesContext }); }}
            title={title || `Show the ${role.toLowerCase()}`}
        >
            <KeyRound size={11} />
            Credential
        </button>
    ) : null);
    // Which route the modal is editing, by index, or null when it is adding one.
    const [editingIndex, setEditingIndex] = useState(null);
    // A ROUTE'S MODULES FOLD AWAY behind a chevron and their count — a route carrying fifteen of them was a wall of
    // chips that pushed every other route down. Closed by default; keyed by route so each opens on its own.
    const [openMods, setOpenMods] = useState(() => new Set());
    const toggleMods = (key) => setOpenMods(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });
    // The chevron + count, the same wherever a list of modules sits (a route's proxy side, a custom code's chips). It
    // stops the click: it sits inside a route that opens its editor, and inside a custom code chip that is a link.
    const modsToggle = (key, paths) => (
        <button
            type="button"
            className="idz-rt-mods-toggle"
            aria-expanded={openMods.has(key)}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleMods(key); }}
            title={openMods.has(key) ? 'Hide the modules' : paths.join(' ')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none',
                     padding: '2px 0', cursor: 'pointer', color: '#4b5563', fontSize: '0.8rem', fontWeight: 600 }}
        >
            {openMods.has(key) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            {paths.length} module{paths.length === 1 ? '' : 's'}
        </button>
    );
    // ONE CUSTOM CODE CHIP, the same in the Sources and the Integration column: its name (and modules), muted when it
    // does not run there. A superadmin's opens the code in a new tab; anyone else's is a label, its tooltip saying why.
    const codeChip = ({ key, off, href, title, children }) => (isSuperAdmin && href ? (
        <a
            key={key}
            className={`idz-rt-int-chip${off ? ' is-off' : ''}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title={`${title}. Opens the code in a new tab.`}
        >
            {children}
        </a>
    ) : (
        <span key={key} className={`idz-rt-int-chip${off ? ' is-off' : ''}`} title={title}>{children}</span>
    ));

    // A PROXY THAT NEEDS A KEY IS GIVEN ONE. A blank field with a Generate button beside it is a question
    // with one sensible answer, asked of everyone who adds a source — and a design saved with it blank
    // produces a proxy that cannot authenticate, discovered only when it is deployed.
    //
    // EVERY PROXIED SOURCE, NOT JUST THE API_KEY ONES.
    //
    // This asked `authentication_scheme === 'API_KEY'`, which is the wrong question. That field says how
    // the hub authenticates TO THE BACKEND — OHIP is HUB_CREDENTIALS because the hub holds Oracle's
    // client id and secret, Pegasus is INBOUND_BASIC, the MCP server is INBOUND_OAUTH. None of that is
    // about the INBOUND door.
    //
    // The proxy key is the source node's ADDRESS on the Integrations API, and every proxy resolves its
    // source by it — `proxy.resolve(mysql, key, …)` in oxi-enqueue, oxi-dequeue, pegasus-enqueue and
    // ohip-enqueue alike. So the question is whether the system has a proxy at all.
    //
    // Asked the old way, only OXI ever got a key: a Pegasus, OHIP or MCP source saved cleanly, looked
    // complete on the board, and then failed at the first call with "this source has no API key yet —
    // save the design so one is stored", on a design that HAD been saved. Two directions of
    // authentication were being decided by one field.
    //
    // ONLY WHERE THERE IS NOTHING TO OVERWRITE: skipped when the node already carries a key, and when the
    // server says one is stored but did not send it (a reader who is not a superadmin).
    useEffect(() => {
        if (readOnly || !onUpdateNode) return;
        for (const node of nodes) {
            if (node.role === 'HUB') continue;
            const system = systemById.get(String(node.marketplace_id));
            if (!system || String(system.uses_proxy).toUpperCase() !== 'Y') continue;
            if (node.proxy_api_key || Number(node.has_proxy_api_key) === 1) continue;
            onUpdateNode(node.id, { proxy_api_key: generateApiKey() });
        }
    }, [nodes, systemById, readOnly, onUpdateNode]);

    const routeModalNode = nodes.find(n => n.id === routeModalNodeId) || null;
    const routeModalSystem = routeModalNode && routeModalNode.role !== 'VENDOR'
        ? systemById.get(String(routeModalNode.marketplace_id)) : null;

    // The inbound credential's name, for a row drawn in the browser whose name has not come back from a
    // save yet.
    const credentialLabel = (credentialId) => {
        const c = (credentials || []).find(x => String(x.id) === String(credentialId));
        return (c && c.credential_name) || null;
    };

    // The backend a credential reaches, for a row whose own value has not come back from the server yet.
    const credentialBackend = (credentialId) => {
        const c = (credentials || []).find(x => String(x.id) === String(credentialId));
        return (c && c.backend_systems) || null;
    };

    // THE BINDING A ROUTE IS POINTED AT (V484). A credential can serve several backends — one Oracle
    // credential covers every property under the chain — so the credential alone never said WHICH one a
    // route reaches. The binding does: backend, credential and the x-hotelid that picks the property out.
    // Read from the freshly-loaded list so a row drawn a moment ago names its backend before the save
    // round-trips.
    const bindingOf = (route) => (access || []).find(a => String(a.id) === String(route.uapi_access_id)) || null;

    // ── A TARGET THAT COLLECTS RATHER THAN BEING CALLED (V516) ──────────────
    //
    // Most backends are called: the Universal API makes the request and waits. A vendor like Thynk does
    // not accept a push at all — it polls GET /mq with the Universal API credential it already holds,
    // and the queue is a table.
    //
    // DRAWN AS A PULL, because the board is read as a picture of what happens. An arrow pointing at
    // Thynk says "we call Thynk", which is a call this platform never makes and a thing somebody will
    // eventually go looking for in a log.
    //
    // FROM THE MARKETPLACE RECORD, by flag: `hub_to_marketplace = 'MQ_POLL'`. The route's own binding
    // first (where it actually lands), then the kind the template is designed for, then the palette —
    // so a template depicts the poll before any property has been bound to it.
    // May this board change the credential a route's target signs in with? (An implementation's board, not read-only,
    // a target we call, on a system we know.) A target that POLLS /mq (Thynk) has none here: it collects as itself, and
    // the hub queues its messages for the vendor granted the hotel on Admin › Vendors (/opt/core/uapi-mq queueRoute).
    const canPickTarget = (route) => !route.to_node_id && !targetPolls(route) && !!onPickTargetCredential && !credentialsReadOnly
        && !!((bindingOf(route) || {}).marketplace_id || route.target_marketplace_id);
    const targetPolls = (route) => {
        const stated = route.backend_delivery || route.target_delivery;
        if (stated) return stated === 'MQ_POLL';
        const system = route.target_marketplace_id
            ? systemById.get(String(route.target_marketplace_id)) : null;
        return !!system && system.hub_to_marketplace === 'MQ_POLL';
    };

    // THE KIND OF BACKEND THE TEMPLATE DESIGNED THIS ROUTE FOR (V510). The server names it; falling
    // back to the palette means a route constrained a moment ago reads correctly before the save
    // round-trips.
    const targetSystemName = (route) => {
        if (route.target_system_name) return route.target_system_name;
        const system = route.target_marketplace_id
            ? systemById.get(String(route.target_marketplace_id)) : null;
        return (system && system.name) || null;
    };

    // HOW THIS TARGET'S RESULT IS VIEWED (V583): the binding's system where there is one, else the kind the route is
    // designed for, else the palette's entry for it — the same precedence as targetPolls.
    const resultViewerOf = (route) => {
        if (route.backend_result_viewer) return route.backend_result_viewer;
        if (route.target_result_viewer) return route.target_result_viewer;
        const system = route.target_marketplace_id ? systemById.get(String(route.target_marketplace_id)) : null;
        return (system && system.result_viewer) || null;
    };

    // A ROUTE BOUND TO A DIFFERENT SYSTEM FROM THE ONE IT IS DESIGNED FOR. The binding decides where calls go;
    // the route's target is only the design. They part when the target is changed on the design after the
    // binding was made (dev, Rotaa Test, Oct 2026: redrawn for Azure Blob, still bound to Opera Cloud, so its
    // test reservations reached OHIP and came back as BLK00093). The uapi proxies refuse such a route
    // (routes.match); this says so on the board, where the picker used to hide it — its options are filtered
    // to the route's system, so a binding of another system matched none and the select fell back to
    // "— any <system> —", which read as correct. null when the two agree or either is unknown.
    const misbound = (route) => {
        if (!route.target_marketplace_id || !route.uapi_access_id || route.to_node_id) return null;
        const binding = bindingOf(route);
        const got = (binding && binding.marketplace_id) || route.access_marketplace_id;
        if (!got || String(got) === String(route.target_marketplace_id)) return null;
        return {
            want: targetSystemName(route) || 'the system it is designed for',
            got: (binding && binding.system_name) || route.backend_systems || 'another system',
            hotelId: (binding && binding.uapi_hotel_id) || route.x_hotelid || `binding #${route.uapi_access_id}`,
        };
    };

    // The logo for where a route lands: the server's value first, then whichever system the route points at.
    const targetLogo = (route) => {
        if (route.backend_image) return route.backend_image;
        if (route.target_system_image) return route.target_system_image;
        const node = route.to_node_id ? nodes.find(x => String(x.id) === String(route.to_node_id)) : null;
        const marketplaceId = node
            ? node.marketplace_id
            : (route.target_marketplace_id || (bindingOf(route) || {}).marketplace_id);
        const system = marketplaceId ? systemById.get(String(marketplaceId)) : null;
        return (node && node.system_image) || (system && system.image) || null;
    };

    const nodeLabel = (id) => {
        const n = nodes.find(x => String(x.id) === String(id));
        return n ? (n.adapter || nodeTitle(n)) : `#${id}`;
    };

    // RENDER HELPERS, CALLED AS FUNCTIONS — never used as <Tags>. They are declared inside this component,
    // so as JSX elements each render would hand React a new component type and it would remount the board,
    // taking the API key input out from under the cursor on every keystroke.

    // THE API KEY THIS PROXY PRESENTS. At the top of the proxy column: it belongs to the proxy, not to any
    // one route, and every route below it is made with it.
    const proxyAuth = ({ node, system }) => {
        if (!system || system.authentication_scheme === undefined) return null;
        // NOT EVERY PROXY HOLDS A KEY OF ITS OWN. A system set to "Stored Credentials on Hub" is reached
        // with a credential already held on the platform — its own username and password, OAuth client or
        // token — so nothing is minted here and there is nothing to copy. WHICH credential has to be said,
        // and it is said with the platform's own picker rather than a list built for this screen: choosing
        // a credential also SHARES it, and that dance exists once, in CredentialSelector.
        // NOTHING TO SIGN IN TO. A system served by the MCP proxy (mcp_enabled, V507) does not have the
        // hub calling IT — the model calls US — so "how this proxy signs in to LLM" is a question with
        // no answer, and the picker offered to create a credential against a marketplace that will
        // never hold one. Its three tabs read 0, 0, 0, which is the screen saying so without saying so.
        //
        // WHAT ACTUALLY AUTHENTICATES THE CALLER is the Universal API credential on this source's
        // routes — the middle column, one cell to the right — and that is where the test token is
        // minted from too. Said here rather than left blank, because an empty box on a board full of
        // credentials reads as one more thing to fill in.
        //
        // ON THE SCHEME, not on mcp_enabled. The MCP flag says which proxy serves a system; the
        // question here is which DIRECTION it authenticates in, and INBOUND_OAUTH (V513) is the
        // marketplace's own answer to it. Keyed that way, the next inbound system gets this without
        // this file being touched — and MCP stays what it is, a proxy, rather than the only reason
        // this branch exists.
        if (system.authentication_scheme === 'INBOUND_OAUTH') {
            return (
                <div className="idz-proxy-cred">
                    <div className="cred-selector-title">System Credential</div>
                    <p className="idz-proxy-cred-na">
                        Not used — <strong>{system.name}</strong> calls the Universal API rather than
                        being called, so this proxy signs in to nothing. What the caller presents is a
                        token from the <strong>Universal API</strong> credential on each route, to the
                        right; the endpoint, grant and scope it needs are the <em>Inbound</em> settings
                        on this system&rsquo;s marketplace record.
                    </p>
                </div>
            );
        }

        if (system.authentication_scheme === 'HUB_CREDENTIALS') {
            const chosen = node.credential_id
                ? { credential_id: node.credential_id, credential_name: node.credential_name }
                : null;
            return (
                <div className="idz-proxy-cred">
                    <CredentialSelector
                        marketplaceId={system.id}
                        hotelId={hotelId}
                        accountId={accountId}
                        partnerId={partnerId}
                        value={chosen}
                        // THE CREDENTIAL AND ITS PER-PROPERTY VALUES IN ONE PICK (overridesInPicker) — the OHIP hotel id
                        // above all: one Oracle OHIP credential serves every property under a chain, so this property's
                        // id is asked in the picker, beside the credential, and both are saved together on Save. The
                        // hotel id goes to its own column (V483), the rest to credential_overrides. WHICH fields appear is
                        // the marketplace's decision (must_be_overridden), read by the shared component.
                        overridesInPicker
                        overrides={joinOverrides(node.hotelid_override, node.credential_overrides)}
                        onChange={(cred, values) => {
                            if (!onUpdateNode) return;
                            const { hotelidOverride, credentialOverrides } = splitOverrides(values || {});
                            onUpdateNode(node.id, {
                                credential_id: cred ? cred.credential_id : null,
                                credential_name: cred ? cred.credential_name : null,
                                hotelid_override: cred ? hotelidOverride : null,
                                credential_overrides: cred ? credentialOverrides : null,
                            });
                        }}
                        canEdit={isSuperAdmin}
                        title="System Credential"
                        subtitle={`How this proxy signs in to ${system.name}. Pick one already held for this property, chain or partner — or add a new one.`}
                        disabled={credentialsReadOnly || !hotelId}
                        disabledReason={credentialsReadOnly
                            ? readOnlyReason
                            : 'Choose a hotel in the top bar first — a credential is shared to a property.'}
                        overridesTitle="For this property"
                    />
                </div>
            );
        }
        if (system.authentication_scheme !== 'API_KEY') {
            return <span className="idz-proxy-auth idz-proxy-auth-none">No authentication</span>;
        }
        const canSeeKey = node.proxy_api_key !== null && node.proxy_api_key !== undefined;
        const isNew = node.has_proxy_api_key === undefined;
        if (!readOnly && onUpdateNode && (canSeeKey || isNew)) {
            return (
                <div className="idz-proxy-auth idz-proxy-auth-edit">
                    <ApiKeyField
                        id={`proxy-api-key-${node.id}`}
                        value={node.proxy_api_key || ''}
                        onChange={(v) => onUpdateNode(node.id, { proxy_api_key: v })}
                        hint="Generated for this proxy and stored encrypted when the design is saved."
                    />
                </div>
            );
        }
        if (canSeeKey && node.proxy_api_key) {
            return (
                <div className="idz-proxy-auth idz-proxy-auth-edit">
                    <ApiKeyField id={`proxy-api-key-${node.id}`} value={node.proxy_api_key} onChange={() => {}} readOnly hint={null} />
                </div>
            );
        }
        if (Number(node.has_proxy_api_key) === 1) return <span className="idz-proxy-auth">API key set</span>;
        return <span className="idz-proxy-auth idz-proxy-auth-missing">API key not set</span>;
    };

    // ONE ROUTE IS ONE CARD, spanning all three columns.
    //
    // The route's three parts — what it carries, the credential it is made with, where that lands — are one
    // thing, so they are bounded as one thing and deleted as one thing. Three separate boxes with an × on
    // the first made the × look like it belonged to the modules.
    //
    // THE CARD CARRIES THE BOARD'S OWN COLUMN TEMPLATE, so its three cells stay under the three headings
    // above it. A card with its own spacing would drift out of line the moment a column resized.
    // WHERE OXI SENDS ITS MESSAGES. The proxy's address with this proxy's key in it — the one thing
    // somebody configuring OPERA has to be given, and until now it existed only in their head.
    //
    // THE INTEGRATIONS API'S OWN FRONT DOOR, not sync's and not the Universal API's. Sync already serves
    // /oxi/{key} for its connectors, but an integration's proxy is not part of a sync project; and OXI is
    // not a Universal API consumer. Its own name, CloudFront distribution and WAF (code-integration).
    //
    // THE HOST COMES FROM CONFIGURATION, NEVER FROM HERE. A plausible-looking URL assembled in the browser
    // is worse than none: it would be pasted into OPERA and fail somewhere nobody is watching.
    const proxyBaseUrl = (process.env.REACT_APP_INTEGRATION_URL || '').replace(/\/+$/, '');

    const renderInboundUrl = (node) => {
        const key = node.proxy_api_key;
        const path = `/oxi/${key || '<api key>'}`;
        const full = proxyBaseUrl ? `${proxyBaseUrl}${path}` : null;
        return (
            <div className="idz-inbound">
                <span className="idz-inbound-label">Inbound URL</span>
                <div className="idz-inbound-row">
                    <code className="idz-inbound-url" title={full || path}>{full || path}</code>
                    {full && key && (
                        <CopyButton value={full} label="" copiedLabel="" title="Copy the inbound URL" className="idz-inbound-copy" />
                    )}
                </div>
                <p className="idz-inbound-hint">
                    {full
                        ? 'Give this to OPERA as the OXI partner address. It carries the key above, so regenerating the key changes this URL.'
                        : 'The Universal API address is not configured in this portal build, so only the path can be shown.'}
                </p>
            </div>
        );
    };

    // WHAT A BOX LOOKS LIKE WHEN A MESSAGE IS PAINTED ON IT, and what clicking it does. Without a run these
    // add nothing at all, so the designer is the designer.
    const runClass = (kind, id) => {
        if (!run || !run.stateOf) return '';
        const state = run.stateOf(kind, id);
        return state ? ` idz-run idz-run-${state}` : ' idz-run idz-run-untouched';
    };
    const runOpen = (kind, id) => {
        if (!run || !run.onOpen) return {};
        const count = run.countOf ? run.countOf(kind, id) : 0;
        if (!count) return {};
        const open = (e) => { e.stopPropagation(); run.onOpen(kind, id); };
        return {
            onClick: open,
            onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(e); } },
            role: 'button',
            tabIndex: 0,
            title: `${count} log ${count === 1 ? 'entry' : 'entries'} here — open them`,
        };
    };

    // ── THE INTEGRATION, between the hub and the target ─────────────────────────────────────────────────
    //
    // The code that turns a Universal API call into the target's own dialect (UAPI-INTEGRATIONS.md) — one
    // per backend system — and the custom code run around it (V528): before() ahead of the call, after()
    // on the answer. It is its own column because it is its own thing: the hub decides whether a call may
    // be made, the integration decides what it becomes, the target receives it. A snippet written for
    // Azure BLOB that names this client's files is invisible from both neighbours.
    //
    // TWO PLACES ATTACH CUSTOM CODE HERE. The template, to the route itself (V533) — it runs for every
    // property implementing the template, and is what this column's Custom Code button edits, like the
    // proxy's beside it. And each property, to its own backend binding (V528), on the Universal API screen.
    // Template snippets run first, so both are listed in that order.
    const renderIntegrationCell = (route) => {
        const polls = targetPolls(route);
        const routeModuleIds = new Set((route.modules || []).map(m => String(m.uapi_route_id)));
        const templateSnippets = route.uapi_route_custom_code || [];
        const propertySnippets = route.uapi_custom_code || [];
        const snippets = [
            ...templateSnippets.map(c => ({ ...c, from: 'template' })),
            ...propertySnippets
                .filter(c => !templateSnippets.some(t => String(t.id) === String(c.id)))
                .map(c => ({ ...c, from: 'property' })),
        ];
        // NO CREDENTIAL, NO INTEGRATION NAMED. Which integration answers follows the credential the route
        // is made with, and on a template that is chosen by each implementation — the same wording as the
        // Universal API cell beside it, for the same reason.
        const unbound = !route.uapi_access_id && !route.credential_id;
        // A snippet runs on this route only if it is switched on AND targeted at a module the route
        // carries (V529). Attached-but-not-running is shown too, muted — "why didn't my code run" is
        // exactly the question this cell has to answer.
        const runsHere = (c) => c.active === 'Y'
            && (c.module_ids || []).some(id => routeModuleIds.has(String(id)));
        const why = (c) => (c.active !== 'Y'
            ? 'inactive — not run'
            : !(c.module_ids || []).length
                ? 'targeted at no module — not run'
                : runsHere(c)
                    ? `runs on ${(c.module_paths || []).filter((p, i) => routeModuleIds.has(String(c.module_ids[i]))).join(', ') || 'this route'}`
                    : `targeted at ${(c.module_paths || []).join(', ')} — none carried by this route`);
        // Where a chip opens: the snippet's own editor, told what it is attached to so Back returns there.
        const snippetHref = (c) => (c.from === 'template'
            ? `/universal-api/custom-code/${c.id}?routeId=${route.id}`
            : `/universal-api/custom-code/${c.id}?accessId=${route.uapi_access_id}`);

        return (
            <div className={`idz-rt-cell idz-rt-integration${runClass('integration', route.id)}`} {...runOpen('integration', route.id)}>
                {unbound && !polls ? (
                    <span className="idz-rt-int-name is-unset">set by the implementation</span>
                ) : route.target_integration_name ? (
                    isSuperAdmin && route.target_integration_id ? (
                        <a
                            className="idz-rt-int-name"
                            href={`/admin/universal-api/integrations/${route.target_integration_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            title="Open this integration's code, in a new tab"
                        >
                            {route.target_integration_name}
                        </a>
                    ) : (
                        <span className="idz-rt-int-name">{route.target_integration_name}</span>
                    )
                ) : (
                    <span className="idz-rt-int-name is-none">
                        {`no integration for ${targetSystemName(route) || 'this system'} yet`}
                    </span>
                )}
                {!unbound && route.target_integration_name && (
                    // WHICH CODE ANSWERS. With no version live a system with a working built-in adapter (the MCP
                    // Backend, OXI — /opt/core/uapi-builtin-adapters) is still served by it; any other answers 501
                    // for every call, which is worth seeing here rather than at the first message.
                    <span
                        className={`idz-rt-int-version${route.target_integration_version || route.target_builtin_adapter ? '' : ' is-none'}`}
                        title={!route.target_integration_version && route.target_builtin_adapter
                            ? `No version of this integration is live, so calls are served by ${route.target_builtin_adapter}`
                            : undefined}
                    >
                        {route.target_integration_version
                            ? `v${route.target_integration_version} live`
                            : route.target_builtin_adapter
                                ? 'built-in adapter — no version live'
                                : 'nothing live — calls answer 501'}
                    </span>
                )}

                {/* A POLLED TARGET IS NEVER CALLED, so neither is its integration or any custom code
                    around it — the message waits on /mq for the vendor. */}
                {polls ? (
                    <span className="idz-rt-int-note">not called — the target polls <code>/mq</code></span>
                ) : snippets.length > 0 && (
                    <span className="idz-rt-int-code">
                        {snippets.map(c => {
                            const label = `${c.name}${c.from === 'template' && propertySnippets.length
                                ? ' (from the template)' : ''} — ${why(c)}`;
                            const chip = (
                                <>
                                    {c.name}
                                    {/* THE SAME CHIPS AS THE PROXY'S MODULES, so a module reads the same
                                        wherever it appears on the board. */}
                                    {(c.module_paths || []).length > 0 && (
                                        <span className="idz-rt-mods idz-rt-int-mods">
                                            {modsToggle(`code-${route.id}-${c.from}-${c.id}`, c.module_paths)}
                                            {openMods.has(`code-${route.id}-${c.from}-${c.id}`)
                                                && c.module_paths.map(p => <span key={p} className="idz-rt-mod">{p}</span>)}
                                        </span>
                                    )}
                                </>
                            );
                            return codeChip({ key: `${c.from}-${c.id}`, off: !runsHere(c), href: snippetHref(c), title: label, children: chip });
                        })}
                    </span>
                )}

                {/* THE SAME BUTTON AS THE PROXY'S, for the code on this side of the hub. Not on a log (a
                    record of one message is read, not changed) and not where the target is polled — no
                    call is made, so nothing would run. SHOWN, GREYED, where it cannot be edited (an
                    implementation's board), as the route's own is: the template's code still runs there, and
                    its count and names (the tooltip) say so — it is changed on the template's Design tab. */}
                {!run && !polls && (
                    <button
                        type="button"
                        className="idz-rt-code"
                        onClick={(e) => { e.stopPropagation(); if (onEditIntegrationCode) onEditIntegrationCode(route); }}
                        disabled={!onEditIntegrationCode}
                        title={templateSnippets.length
                            ? templateSnippets.map((c, n) => `${n + 1}. ${c.name}${c.active === 'Y' ? '' : ' (inactive)'}`).join('\n')
                            : 'No custom code runs around this integration yet'}
                    >
                        <Code2 size={12} />
                        Custom Code{templateSnippets.length ? ` (${templateSnippets.length})` : ''}
                    </button>
                )}
            </div>
        );
    };

    const routeRow = ({ route, index }) => {
        const editable = !readOnly && !!onRoutesChange;
        const mods = route.modules || [];
        // The proxy the route leaves from — its adapter is what a derived name starts with (oxi-proxy → oxi).
        const fromNode = nodes.find(n => String(n.id) === String(route.from_node_id)) || null;
        // A row the log added for a message the custom code made (IntegrationLogTable, code_route_id) shows that
        // route's code — it is what the message came from.
        const codeRouteId = route.code_route_id ?? route.id;
        const snippets = (customCode || []).filter(c =>
            (c.route_ids || []).some(id => String(id) === String(codeRouteId)));
        return (
            <div
                className={`idz-route-card${route.active === 'Y' ? '' : ' is-off'}${editable ? ' is-clickable' : ''}`}
                key={route.id ?? `new-${index}`}
                // THE ROW OPENS ITSELF. It is the thing that was created by the modal, so it is edited by
                // the modal; the controls inside it stop the click, since each of them does something else.
                onClick={editable ? () => { setEditingIndex(index); setRouteModalNodeId(route.from_node_id); } : undefined}
                role={editable ? 'button' : undefined}
                tabIndex={editable ? 0 : undefined}
                onKeyDown={editable ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setEditingIndex(index);
                        setRouteModalNodeId(route.from_node_id);
                    }
                } : undefined}
                title={editable ? 'Edit this route' : undefined}
            >
                <div className={`idz-rt-cell idz-rt-left${runClass('route', route.id)}`} {...runOpen('route', route.id)}>
                    {/* DELETE, top right of the source box — where a box is closed. It removes the whole route
                        (all three boxes), so it sits on the box that owns the route, not on the target. */}
                    {!readOnly && onRoutesChange && (
                        <button
                            type="button"
                            className="idz-rt-x"
                            title="Delete this route"
                            aria-label="Delete this route"
                            onClick={(e) => { e.stopPropagation(); onRoutesChange(routes.filter((_, j) => j !== index)); }}
                        >
                            <X size={14} />
                        </button>
                    )}
                    {/* THE ROUTE'S STATE, top left — read first, before what it carries. */}
                    {!readOnly && onRoutesChange && (
                        <div className="idz-rt-actions">
                            {/* Off by default (V478): a route that carried traffic the moment it was drawn
                                would be a decision made by drawing. */}
                            <button
                                type="button"
                                className={`idz-route-toggle${route.active === 'Y' ? ' is-on' : ''}`}
                                onClick={(e) => { e.stopPropagation(); onRoutesChange(routes.map((x, j) =>
                                    j === index ? { ...x, active: x.active === 'Y' ? 'N' : 'Y' } : x)); }}
                            >
                                {route.active === 'Y' ? 'Active' : 'Inactive'}
                            </button>
                        </div>
                    )}
                    <div className="idz-rt-main">
                        {/* THE ROUTE'S NAME, read before what it carries: it is how this route is referred
                            to everywhere the canvas is not — its custom code, the test, the logs. */}
                        <span className="idz-rt-name" title={routeLabel(route, fromNode && fromNode.adapter)}>
                            {routeLabel(route, fromNode && fromNode.adapter)}
                        </span>
                        {/* ONE CHIP PER MODULE, each removable. Joined into a string they could only be
                            changed by deleting the whole route and building it again, which also threw away
                            its custom code. */}
                        {mods.length > 0 && modsToggle(`route-${route.id ?? `new-${index}`}`, mods.map(m => m.uapi_route_path || m.name))}
                        <span className="idz-rt-mods">
                            {mods.length === 0 && <span className="idz-rt-none">nothing yet</span>}
                            {openMods.has(`route-${route.id ?? `new-${index}`}`) && mods.map(m => (
                                <span className="idz-rt-mod" key={m.uapi_route_id} title={m.name || m.uapi_route_path}>
                                    {m.uapi_route_path || m.name}
                                    {!readOnly && onRoutesChange && (
                                        <button
                                            type="button"
                                            className="idz-rt-mod-x"
                                            title={`Stop carrying ${m.uapi_route_path || m.name} on this route`}
                                            onClick={(e) => { e.stopPropagation(); onRoutesChange(routes.map((x, j) => j === index
                                                ? { ...x, modules: (x.modules || []).filter(y => String(y.uapi_route_id) !== String(m.uapi_route_id)) }
                                                : x)); }}
                                        >
                                            <X size={10} />
                                        </button>
                                    )}
                                </span>
                            ))}
                        </span>
                        {/* THE ROUTE'S CUSTOM CODE, AS THE INTEGRATION'S IS SHOWN — a chip per snippet, on the design and
                            on a log alike, muted when it is switched off. It runs in the source adapter on every record
                            the route carries, so it has no modules of its own to list. */}
                        {snippets.length > 0 && (
                            <span className="idz-rt-int-code idz-rt-route-code">
                                {snippets.map(c => codeChip({
                                    key: `route-${c.id}`,
                                    off: c.active !== 'Y',
                                    href: c.integration_id ? `/pipeline-templates/${c.integration_id}/routes/${codeRouteId}/code` : null,
                                    title: `${c.name} — ${c.active !== 'Y' ? 'inactive — not run'
                                        : route.code_route_id != null ? 'added this message' : 'runs on every record this route carries'}`,
                                    children: c.name,
                                }))}
                            </span>
                        )}
                        {/* NOT ON A LOG. Custom code is something you go and change; a record of one message
                            is something you read. The button would be the only thing on the board that did
                            not describe what happened. */}
                        {!run && <button
                            type="button"
                            className="idz-rt-code"
                            onClick={(e) => { e.stopPropagation(); if (onEditCode) onEditCode(route); }}
                            disabled={!onEditCode}
                            title={snippets.length
                                ? snippets.map((c, n) => `${n + 1}. ${c.name}${c.active === 'Y' ? '' : ' (inactive)'}`).join('\n')
                                : 'No custom code runs on this route yet'}
                        >
                            <Code2 size={12} />
                            Custom Code{snippets.length ? ` (${snippets.length})` : ''}
                        </button>}
                    </div>
                </div>

                {/* BOTH WAYS. A proxy calls the Universal API and reads the answer back; one arrowhead would
                    say the traffic only goes one way. */}
                <div className="idz-rt-arrow"><ArrowLeftRight size={14} /></div>
                <div className={`idz-rt-cell idz-rt-cred${runClass('hub', route.id)}`} {...runOpen('hub', route.id)}>
                    {/* AS WHOM THIS ROUTE IS MADE, CHOSEN HERE when the board is an implementation's.
                        A <select> rather than the full CredentialSelector: these are Universal API
                        credentials already issued to this property, so there is nothing to share and
                        nothing to create — the only question is which one, and a picker that also
                        offered to mint credentials would be answering a question nobody asked. */}
                    {uapiCredential ? (
                        <>
                            {/* THE IMPLEMENTATION'S CALLER IS SET ONCE, ABOVE THE FIRST BOX — so the route shows only
                                what is its own: the x-hotelid of the binding it lands through. */}
                            {(route.x_hotelid || (bindingOf(route) || {}).uapi_hotel_id) ? (
                                <span className="idz-rt-hotelid"
                                      title="The x-hotelid the Universal API presents to the backend for this route">
                                    x-hotelid <code>{route.x_hotelid || bindingOf(route).uapi_hotel_id}</code>
                                </span>
                            ) : (
                                <span className="idz-rt-cred-name">no x-hotelid — choose the binding</span>
                            )}
                            {/* A ROUTE STILL MADE AS ANOTHER CALLER — saved before V649. The next save aligns it. */}
                            {isSuperAdmin && route.credential_id && uapiCredential.id
                                && String(route.credential_id) !== String(uapiCredential.id) && (
                                <span className="idz-rt-misbound" title="Every route is made as the implementation's credential once it is saved again">
                                    <AlertTriangle size={12} />
                                    Made as {route.credential_name || `credential #${route.credential_id}`}, not the
                                    pipeline&rsquo;s credential.
                                </span>
                            )}
                        </>
                    ) : (<>
                    {!credentialsReadOnly && onUpdateRoute ? (
                        <select
                            className="idz-rt-cred-select"
                            value={route.credential_id || ''}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => onUpdateRoute(route, {
                                credential_id: e.target.value ? Number(e.target.value) : null,
                            })}
                            title="The Universal API credential this route is made with"
                        >
                            <option value="">— no credential —</option>
                            {(credentials || []).map(c => (
                                <option key={c.id} value={c.id}>{c.credential_name}</option>
                            ))}
                        </select>
                    ) : (
                    <span className="idz-rt-cred-name">
                        {/* THE INBOUND CREDENTIAL — what the proxy presents to the Universal API. Never
                            the binding's credential_name: that is the BACKEND credential the gateway
                            signs in to OHIP with, and showing it here would name the wrong end of the
                            call. */}
                        {route.credential_name
                            || credentialLabel(route.credential_id)
                            || (route.credential_id
                                ? `credential #${route.credential_id}`
                                : 'set by the implementation')}
                    </span>
                    )}

                    {/* OPEN THE CREDENTIAL AND SHOW IT — the dropdown above chooses it; this opens it in the shared
                        credential editor without leaving a half-drawn design. Nothing to show on a template route: it
                        has no credential until an implementation. */}
                    {credButton(route.credential_id, 'Universal API credential', {
                        title: 'Show the Universal API credential this route is made with',
                    })}
                    </>)}
                    {/* WHAT THIS ROUTE'S CALLER MAY REACH, for information — AS THE GATEWAY DECIDES IT
                        (/opt/core/uapi-entitlement): the binding's override, else the access profile or the
                        credential's own grants, whichever the credential's mode names. It is granted on the
                        Universal API screen, not here, and a route carrying a module the caller cannot reach
                        is a mismatch worth seeing while the route is being drawn rather than at the first call.
                        A module the route carries is marked; the rest are listed quietly beside them. */}
                    {(() => {
                        const grant = routeGrants[routeGrantKey(route)];
                        if (!grant) return null;
                        const from = grant.source === 'profile' && grant.profileName
                            ? `from the access profile "${grant.profileName}"`
                            : (GRANT_SOURCE[grant.source] || '');
                        // A mode with nothing in it grants nothing — the gateway refuses every call — so it is
                        // said, not left blank (PROFILE mode with no profile covering the credential, say).
                        if (!(grant.modules || []).length) {
                            return (
                                <span className="idz-rt-grants" title={`Nothing is granted ${from || ''} — every call is refused`}>
                                    <span className="idz-rt-grant">
                                        {grant.source === 'profile' && !grant.profileName ? 'no access profile' : 'nothing granted'}
                                    </span>
                                </span>
                            );
                        }
                        // Folded like the route's own modules — the same chevron and count (modsToggle).
                        const grantsKey = `grants-${route.id ?? routeGrantKey(route)}`;
                        return (
                            <span className="idz-rt-grants" title={`What this route's caller may reach — ${from}`}>
                                {modsToggle(grantsKey, grant.modules.map(g => g.path))}
                                {openMods.has(grantsKey) && grant.modules.map(g => {
                                    const carried = (route.modules || [])
                                        .some(m => String(m.uapi_route_id) === String(g.uapi_route_id));
                                    const part = g.partial ? ' (some operations only)' : '';
                                    return (
                                        <span
                                            key={g.uapi_route_id}
                                            className={`idz-rt-grant${carried ? ' is-carried' : ''}`}
                                            title={carried
                                                ? `${g.path}${part} — granted ${from}, and carried by this route`
                                                : `${g.path}${part} — granted ${from}, not carried by this route`}
                                        >
                                            {g.path}
                                        </span>
                                    );
                                })}
                            </span>
                        );
                    })()}
                </div>
                <div className="idz-rt-arrow" aria-hidden="true"><ArrowLeftRight size={14} /></div>
                {renderIntegrationCell(route)}
                {/* WHICH WAY THE MESSAGE ACTUALLY MOVES (V516). Both ways for a backend the Universal
                    API calls and reads the answer from; ONE WAY, back towards the hub, for a target
                    that polls — nothing travels rightwards to it, it comes and takes. */}
                <div className="idz-rt-arrow" title={targetPolls(route)
                    ? 'This target is never called — it polls GET /mq and collects its own messages'
                    : undefined}>
                    {targetPolls(route)
                        ? <ArrowLeft size={14} className="idz-rt-arrow-poll" />
                        : <ArrowLeftRight size={14} />}
                </div>
                <div className={`idz-rt-cell idz-rt-target${targetPolls(route) ? ' is-polled' : ''}${misbound(route) ? ' is-misbound' : ''}${runClass('target', route.id)}`} {...runOpen('target', route.id)}>
                    {/* WHAT IT ACTUALLY REACHES. The credential is bound to a backend, so the row names it —
                        "resolved by the credential" was true and useless, and it is only reached now when
                        the property has no binding at all, which is a gap worth seeing. */}
                    {/* THE BACKEND'S MARKETPLACE LOGO, as the System column shows the source's. From the route
                        row (backend_image), else the binding's or target node's system in the palette — a
                        route drawn in the browser has not been round-tripped yet. A broken image hides
                        itself; the name below still says what it is. */}
                    {targetLogo(route) && (
                        <img
                            className="idz-sys-logo idz-rt-target-logo"
                            src={targetLogo(route)}
                            alt=""
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                    )}
                    {/* WHICH OF THIS PROPERTY'S BACKENDS IT LANDS ON. Narrowed to the system the
                        template designed this route for (V510): a route written for Opera Cloud must
                        not be pointed at RMS, and the server refuses it anyway — offering it here would
                        only be a longer way to be told no. */}
                    {!credentialsReadOnly && onUpdateRoute && !route.to_node_id ? (
                        <select
                            className="idz-rt-cred-select"
                            value={route.uapi_access_id || ''}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => onUpdateRoute(route, {
                                uapi_access_id: e.target.value ? Number(e.target.value) : null,
                            })}
                            title="The backend binding this route lands through"
                        >
                            <option value="">
                                {route.target_marketplace_id
                                    ? `— any ${targetSystemName(route) || 'binding'} —`
                                    : '— resolved by the credential —'}
                            </option>
                            {(access || [])
                                .filter(a => !route.target_marketplace_id
                                    || String(a.marketplace_id) === String(route.target_marketplace_id))
                                // Only the implementation's caller's bindings: the Universal API refuses a call made
                                // as one credential with another's x-hotelid.
                                .filter(a => !uapiCredential || !uapiCredential.id || !a.uapi_credential_id
                                    || String(a.uapi_credential_id) === String(uapiCredential.id))
                                .map(a => (
                                    <option key={a.id} value={a.id}>
                                        {a.label || a.system_name || `Binding #${a.id}`}
                                        {a.uapi_hotel_id ? ` · ${a.uapi_hotel_id}` : ''}
                                    </option>
                                ))}
                            {/* The binding it actually has, when that is another system's — shown, selected and
                                labelled, rather than filtered away (see misbound). Choosing anything else
                                re-points it; this option is only ever the current value. */}
                            {misbound(route) && (
                                <option value={route.uapi_access_id}>
                                    {`⚠ ${misbound(route).got} · ${misbound(route).hotelId} — wrong system`}
                                </option>
                            )}
                        </select>
                    ) : (
                    <span className="idz-rt-target-name">
                        {route.to_node_id
                            ? nodeLabel(route.to_node_id)
                            : (route.backend_systems
                                || (bindingOf(route) || {}).system_name
                                // THE KIND IT IS DESIGNED FOR (V510), when no implementation has picked
                                // the actual one yet. Naming the system is the whole point of the
                                // constraint — a template that says "set by the implementation" where it
                                // has in fact decided is hiding the decision it made.
                                || targetSystemName(route)
                                || credentialBackend(route.credential_id)
                                // Genuinely open: an implementation may point this anywhere the property
                                // is bound. The design being reusable, not unfinished.
                                || (route.credential_id
                                    ? 'no backend bound to this route'
                                    : 'any — set by the implementation'))}
                    </span>
                    )}

                    {misbound(route) && (
                        <span
                            className="idz-rt-misbound"
                            title="The proxy will not send on this route until its binding matches the route's system"
                        >
                            <AlertTriangle size={12} />
                            Bound to {misbound(route).got} — this route is for {misbound(route).want}. Nothing is sent
                            on it until it is re-pointed to a {misbound(route).want} binding.
                        </span>
                    )}

                    {/* THE INTEGRATION'S CREDENTIAL — what this route's binding signs in to the backend with, with the
                        binding's own overrides (its x-hotelid and per-property values) shown as used. */}
                    {/* THE TARGET'S CREDENTIAL, PICKED HERE (an implementation's board) — the shared picker, for the system
                        the route lands on. Choosing one binds it: the route's binding takes it, or a binding is made for
                        it and with it the route's x-hotelid. A target that polls /mq has no credential of its own. */}
                    {!route.to_node_id && bindingOf(route) && (() => {
                        // THE BINDING'S CREDENTIAL AND THE VALUES IT IS USED WITH HERE — on the row, not only behind the
                        // button: a chain-level OHIP credential is half an answer without this property's HOTELID.
                        const b = bindingOf(route);
                        const values = overridesOf(b.credential_overrides, b.hotelid_override);
                        return (
                            <>
                                {b.credential_name && <span className="idz-rt-cred-name">{b.credential_name}</span>}
                                {Object.keys(values).length > 0 && (
                                    <span className="idz-rt-overrides" title={`Used with this credential by this ${b.system_name || 'backend'} binding`}>
                                        {Object.entries(values).map(([k, v]) => (
                                            <span key={k} className="idz-rt-override"><span>{k}</span> <code>{String(v)}</code></span>
                                        ))}
                                    </span>
                                )}
                            </>
                        );
                    })()}
                    <span className="idz-rt-cred-actions">
                        {/* ONE CREDENTIAL BUTTON. Where the credential can be changed here, the picker below is the button
                            (red unset / green set) and opens on the bound credential and its overrides; this view-only
                            one is for boards that cannot change it. Both showed side by side, labelled alike. */}
                        {!route.to_node_id && bindingOf(route) && !canPickTarget(route) && credButton(bindingOf(route).credential_id, 'Integration credential', {
                            overrides: overridesOf(bindingOf(route).credential_overrides, bindingOf(route).hotelid_override),
                            overridesContext: `by this ${bindingOf(route).system_name || 'backend'} binding`,
                            title: `Show the credential the ${(bindingOf(route).system_name || 'backend')} binding signs in with`,
                        })}
                        {/* THE TARGET'S CREDENTIAL, PICKED HERE (an implementation's board) — the shared picker, for the
                            system the route lands on, with the per-property overrides it needs. Choosing one binds it: the
                            route's binding takes it, or a binding is made for it and with it the route's x-hotelid. A target
                            that polls /mq has no credential of its own. */}
                        {canPickTarget(route) && (
                            <button
                                type="button"
                                className={`idz-rt-cred-link idz-cred-view${credState((bindingOf(route) || {}).credential_id)}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const b = bindingOf(route);
                                    setTargetPick({
                                        route,
                                        marketplaceId: (b && b.marketplace_id) || route.target_marketplace_id,
                                        systemName: (b && b.system_name) || targetSystemName(route) || 'the target',
                                        selected: b && b.credential_id
                                            ? { credential_id: b.credential_id, credential_name: b.credential_name, _entityType: bindingLevel(b) }
                                            : null,
                                        values: b ? joinOverrides(b.hotelid_override, b.credential_overrides) : {},
                                    });
                                }}
                                title={bindingOf(route)
                                    ? `Change the credential the ${bindingOf(route).system_name || 'backend'} binding signs in with, or its overrides`
                                    : `Choose the credential this route signs in to ${targetSystemName(route) || 'the target'} with`}
                            >
                                <KeyRound size={11} />
                                Credential
                            </button>
                        )}
                    </span>

                    {/* HOW IT GETS THERE, where that is not "we call it". Said on the row rather than
                        left to the arrow alone: the arrow is a glyph somebody has to already know, and
                        this is the difference between a message that was sent and one that is waiting
                        to be collected — which is the first question when a vendor says it never
                        arrived. */}
                    {targetPolls(route) && (
                        <span className="idz-rt-polls" title="Queued on the Universal API. The vendor polls GET /mq with its own credential and takes what is waiting.">
                            polls <code>/mq</code>
                        </span>
                    )}

                    {/* WHAT IS ACTUALLY WAITING. For a target that is never called there is no response
                        to look at, so the queue is the only place "did it arrive?" can be answered.
                        Needs the route's BINDING — which is what identifies this property's queue — so
                        it appears only where one exists. */}
                    {targetPolls(route) && onViewQueue && route.uapi_access_id && (
                        <button
                            type="button"
                            className="idz-rt-queue"
                            onClick={(e) => { e.stopPropagation(); onViewQueue(route); }}
                            title="What this target has not collected yet"
                        >
                            <Inbox size={11} /> View queue
                        </button>
                    )}

                    {/* THE RESULT, FOR A TARGET THAT STORES (V583). Some targets answer nothing worth reading —
                        Azure Blob says "stored" — so what they hold is the result. The system's Result Viewer
                        (Admin > Marketplace > ESB Integration) says how to look at it; FILE_VIEWER browses the
                        files through this property's binding. Needs the binding, so implementations only. */}
                    {resultViewerOf(route) === 'FILE_VIEWER' && onViewFiles && route.uapi_access_id && !misbound(route) && (
                        <button
                            type="button"
                            className="idz-rt-queue"
                            onClick={(e) => { e.stopPropagation(); onViewFiles(route); }}
                            title="What this target is holding for this property"
                        >
                            <FolderOpen size={11} /> View files
                        </button>
                    )}

                    {/* THE x-hotelid, ON THE ROW. It is half the address: the credential says who is
                        calling, this says which property at the backend answers. Two routes on the same
                        credential and different properties are otherwise drawn identically, and the one
                        sending Hilton's traffic to the wrong resort looks exactly like the one that is
                        right. */}
                    {!uapiCredential && (route.x_hotelid || (bindingOf(route) || {}).uapi_hotel_id) && (
                        <span
                            className="idz-rt-hotelid"
                            title="The x-hotelid the Universal API presents to the backend for this route"
                        >
                            x-hotelid <code>{route.x_hotelid || bindingOf(route).uapi_hotel_id}</code>
                        </span>
                    )}
                </div>
            </div>
        );
    };

    // THE HEADINGS BELONG TO THE SCREEN, NOT TO EACH BOARD. With one board per proxy they were repeated
    // down the page — three identical titles per proxy — which reads as three separate diagrams rather than
    // one picture with several sources in it. Said once, above everything, on the same column template so
    // every board below lines up with them.
    // ADD SOURCE AND ADD VENDOR, side by side — beside the column heading, or alone on an empty design. A vendor is chosen
    // in place from the partner's vendors (STYLE.md: no modals); one already on the design is not offered twice.
    const renderAddButtons = () => (
        <div className="idz-add-group">
            <button type="button" className="idz-add idz-add-inline" onClick={() => onAdd('SOURCE')}>
                <Plus size={12} /> Add source
            </button>
            {onAddVendor && (vendorPick ? (
                <select
                    className="idz-rt-cred-select idz-vendor-select"
                    autoFocus
                    value=""
                    onBlur={() => setVendorPick(false)}
                    onChange={(e) => {
                        const v = vendorsToAdd.find(x => String(x.id) === e.target.value);
                        setVendorPick(false);
                        if (v) onAddVendor(v);
                    }}
                >
                    <option value="">{vendorsToAdd.length ? '— choose a vendor —' : '— every vendor is on the design —'}</option>
                    {vendorsToAdd.map(v => (
                        <option key={v.id} value={v.id}>{v.name}{v.system_name ? ` (${v.system_name})` : ''}</option>
                    ))}
                </select>
            ) : (
                <button type="button" className="idz-add idz-add-inline idz-add-vendor"
                    disabled={!(vendors || []).length}
                    title={(vendors || []).length
                        ? 'Add a vendor that calls the Universal API itself — collecting from /mq, or reading and writing a target'
                        : 'The partner that owns this template has no vendors — add one on Partner Settings › Vendors'}
                    onClick={() => setVendorPick(true)}>
                    <Plus size={12} /> Add vendor
                </button>
            ))}
        </div>
    );

    const renderHeadings = () => (
        <div className="idz-headings">
            {/* EACH COLUMN SAYS WHAT IT IS. Only the hub carried a line of explanation, so the other two
                read as bare labels beside a paragraph — and the one column that needs least explaining was
                the only one getting any. */}
            <div className="idz-board-head idz-c1 idz-head-sys">
                <span className="idz-board-title">System</span>
                {!run && <span className="idz-headings-note">What sends.</span>}
            </div>
            <div className="idz-board-head idz-c3">
                <div className="idz-headings-text">
                    <span className="idz-board-title">Sources and Vendors</span>
                    {!run && <span className="idz-headings-note">
                        The proxy that speaks the system's protocol, or the vendor that calls the Universal API itself,
                        and the routes each carries.
                    </span>}
                </div>
                {!readOnly && renderAddButtons()}
            </div>
            <div className="idz-board-head idz-c5">
                <span className="idz-board-title">{HUB_LABEL}</span>
                {!run && <span className="idz-headings-note">
                    Access profiles and redactions apply here. Every message crosses it.
                </span>}
                {/* THE ONE CREDENTIAL THIS IMPLEMENTATION CALLS AS (V649), above the first box. Superadmin only. */}
                {uapiCredential && isSuperAdmin && (
                    <div className="idz-uapi-cred">
                        <button type="button" className={`idz-uapi-cred-toggle${credState(uapiCredential.id)}`} aria-expanded={uapiCredOpen}
                                title={uapiCredential.name || undefined}
                                onClick={() => setUapiCredOpen(o => !o)}>
                            {uapiCredOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            <KeyRound size={12} />
                            Credential
                            {!uapiCredOpen && <span className="idz-uapi-cred-name">
                                {uapiCredential.name || (uapiCredential.id ? `#${uapiCredential.id}` : 'not set')}
                            </span>}
                        </button>
                        {uapiCredOpen && (
                            <div className="idz-uapi-cred-body">
                                {!credentialsReadOnly && uapiCredential.onChange ? (
                                    <select
                                        className="idz-rt-cred-select"
                                        value={uapiCredential.id || ''}
                                        onChange={(e) => uapiCredential.onChange(e.target.value ? Number(e.target.value) : null)}
                                        title="The Universal API credential every route of this implementation is made with"
                                    >
                                        <option value="">— no credential —</option>
                                        {(credentials || []).map(c => (
                                            <option key={c.id} value={c.id}>{c.credential_name}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <span className="idz-rt-cred-name">
                                        {uapiCredential.name || (uapiCredential.id ? `credential #${uapiCredential.id}` : 'not set')}
                                    </span>
                                )}
                                {credButton(uapiCredential.id, 'Universal API credential', {
                                    title: 'Show the Universal API credential this implementation is made with',
                                })}
                                {/* GENERATE (none yet) / REGENERATE (a new client id + secret on the one it has). */}
                                {!credentialsReadOnly && uapiCredential.onIssue && (
                                    <button type="button" className="idz-rt-cred-link idz-cred-view"
                                        disabled={uapiCredential.issuing}
                                        onClick={() => uapiCredential.onIssue(uapiCredential.id ? 'regenerate' : 'generate')}
                                        title={uapiCredential.id
                                            ? 'Give this credential a new client id and secret'
                                            : 'Issue a new Universal API credential for this implementation'}>
                                        <RefreshCw size={12} />
                                        {uapiCredential.issuing ? 'Working…' : (uapiCredential.id ? 'Regenerate' : 'Generate')}
                                    </button>
                                )}
                                <span className="idz-headings-note">
                                    Every route is made as this credential; each differs only by its x-hotelid.
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>
            <div className="idz-board-head idz-c7">
                <span className="idz-board-title">Integration</span>
                {!run && <span className="idz-headings-note">
                    The code that speaks the target's language, and the custom code this property runs
                    around it.
                </span>}
            </div>
            <div className="idz-board-head idz-c9">
                <span className="idz-board-title">Targets</span>
                {!run && <span className="idz-headings-note">
                    Where each route lands, decided by the credential it is made with. A target marked
                    {' '}<em>polls /mq</em> is never called — it collects its own messages.
                </span>}
            </div>
        </div>
    );

    // LAST CONTACT, IN EACH DIRECTION THE SYSTEM USES (V488 / V489). The same To / From heartbeats the sync
    // project page shows, and through the same component. Which appear is the marketplace's decision:
    // Marketplace to Hub = HTTP Push shows To, Hub to Marketplace = HTTP Pull shows From. A system that
    // does neither shows nothing rather than a badge that can only ever say "never".
    // Read from the saved node first, then the system record for a node added in the browser.
    // Does this source talk to the hub over HTTP at all (Marketplace to Hub / Hub to Marketplace, V488)?
    //
    // HTTP_POLL IS A THIRD ANSWER, not a variant of the first two (V520). HTTP_PUSH means the system
    // sends to us and HTTP_PULL means it collects from us — both are the system calling. HTTP_POLL is
    // the hub calling OUT, which is how Opera Cloud works: nothing arrives unless we go and fetch it.
    // Left out of this test, an OHIP source showed no Test button and no contact badge at all, which
    // read as "this source does not communicate" for the one source that communicates most.
    const hasHubTransport = (src, system) => {
        const toHub = src.marketplace_to_hub || (system && system.marketplace_to_hub);
        const fromHub = src.hub_to_marketplace || (system && system.hub_to_marketplace);
        return toHub === 'HTTP_PUSH' || toHub === 'HTTP_POLL' || fromHub === 'HTTP_PULL';
    };


    const renderContact = (src, system) => {
        const toHub = src.marketplace_to_hub || (system && system.marketplace_to_hub);
        const fromHub = src.hub_to_marketplace || (system && system.hub_to_marketplace);
        // A POLLED SOURCE STILL SHOWS "To": the badge answers "when did anything last arrive from this
        // system", which is the same question whichever end placed the call. The title says which end
        // that was, so the mechanism is not lost — it is just not a third badge nobody asked for.
        const polled = toHub === 'HTTP_POLL';
        const showPush = toHub === 'HTTP_PUSH' || polled;
        const showPull = fromHub === 'HTTP_PULL';
        if (!showPush && !showPull) return null;
        const name = src.system_name || nodeTitle(src);
        return (
            <span className="idz-sys-contact">
                {showPush && (
                    <ContactBadge icon={Upload} label="To"
                        title={polled ? `${name} to HCONNECT (HCONNECT polls ${name})` : `${name} to HCONNECT (HTTP push)`}
                        lastContactMs={src.http_push_last_contact_ms} />
                )}
                {showPull && (
                    <ContactBadge icon={Download} label="From" title={`${name} from HCONNECT (HTTP pull)`}
                        lastContactMs={src.http_pull_last_contact_ms} />
                )}
            </span>
        );
    };

    const renderBoard = (src) => {
        // A VENDOR (V704): no system, no proxy — the vendor itself, in its own colour, calling the Universal API.
        const isVendor = src.role === 'VENDOR';
        const system = isVendor ? null : systemById.get(String(src.marketplace_id));
        const isOpen = expanded.has(src.id);
        const mine = routes
            .map((route, index) => ({ route, index }))
            .filter(({ route }) => String(route.from_node_id) === String(src.id));
        // ON A LOG, ONE MESSAGE CAME IN THROUGH ONE SOURCE. A source with no step anywhere — its system, its proxy or
        // any of its routes — is another channel of this design, unused by this message: faded right back and labelled,
        // so it is not read as part of the story (its untouched boxes were the same grey as a target deliberately not
        // called on the channel that was used).
        const has = (kind, id) => !!(run && run.stateOf && run.stateOf(kind, id));
        const otherChannel = !!run && !has('system', src.id) && !has('proxy', src.id)
            && !mine.some(({ route }) => has('route', route.id));

        // THE COLUMNS ARE TINTED BANDS BEHIND EVERYTHING, one colour each. A route card sits on top of them
        // with no fill of its own, so the card still reads as one unit while each of its three parts takes
        // the colour of the column it belongs to. Boxing each part separately would say they were three
        // things.
        return (
            <div className={`idz-board${isVendor ? ' idz-board-vendor' : ''}${otherChannel ? ' idz-board-other' : ''}`} key={src.id}
                title={otherChannel ? `Not part of this message — it came in through another source, not ${src.system_name || nodeTitle(src)}` : undefined}>
                {otherChannel && <span className="idz-board-other-label">Not part of this message</span>}
                {/* A LAYER BEHIND THE BOARD, not three grid items. As grid items the tints have to
                    span the rows, and the rows here are all implicit — `1 / -1` names the explicit grid,
                    which is empty, so each band collapsed to a hairline at the top. This layer is
                    stretched over the whole board and carries the same column template, so the tints are
                    full height whatever the content does. */}
                {/* PURELY DECORATIVE, and entirely out of flow. The System box used to live in here too,
                    and it is the one thing in this layer with real content — a logo, a name, the
                    contact badges and the Test button. Absolutely positioned, none of that counted
                    towards the board's height, so a board with no routes collapsed to the height of its
                    proxy header while the System box carried on below it, over whatever came next. It
                    is a grid item of the board now; only the three tints remain here. */}
                <div className="idz-bands" aria-hidden="true">
                    <span />
                    <span />
                    <span className="idz-band idz-band-src" />
                    <span />
                    <span className="idz-band idz-band-hub" />
                    <span />
                    <span className="idz-band idz-band-int" />
                    <span />
                    <span className="idz-band idz-band-tgt" />
                </div>

                {/* THE SENDING SYSTEM — OPERA OXI for an oxi-proxy. It was only small print under the
                    proxy's name, so the picture started at the proxy and the thing actually sending was
                    missing from it. One per board, centred down the column.
                    A REAL GRID ITEM (column 1), so its height is the board's height. */}
                {!isVendor && <>
                <span className={`idz-band idz-band-sys idz-c1${runClass('system', src.id)}`} {...runOpen('system', src.id)}>
                    <span className="idz-src-kind">System</span>
                    {/* THE MARKETPLACE LOGO (SYSTEMIMAGEURL). From the saved node, or from the system
                        record for a node added in the browser that has not been saved yet. A broken
                        image hides itself — the name beneath it still says what the system is. */}
                    {(src.system_image || (system && system.image)) && (
                        <img
                            className="idz-sys-logo"
                            src={src.system_image || system.image}
                            alt=""
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                    )}
                    <span className="idz-sys-name">{src.system_name || nodeTitle(src)}</span>
                    {/* LIVE STATUS, NOT PART OF A RECORD. When the picture is one message's story, "last
                        contact 1h ago" is about now, not about then. */}
                    {!run && renderContact(src, system)}
                    {/* TEST THE SOURCE'S OXI ENDPOINT — push a message or poll, through the real
                        Integrations API. Only for a system that talks to the hub over HTTP (V488), and
                        only where the caller can act on it. */}
                    {onTestSource && hasHubTransport(src, system) && (
                        <button
                            type="button"
                            className="idz-sys-test"
                            onClick={() => onTestSource(src)}
                            title={`Send a test message through ${src.adapter || nodeTitle(src)}`}
                        >
                            <FlaskConical size={13} /> Test
                        </button>
                    )}
                    {/* RESEND, ON A LOG'S BOARD. The message this record is about, sent through the proxy
                        again from the original as received — offered where the message entered, on the
                        system that sent it. */}
                    {run && run.onResend && !otherChannel && (
                        <button
                            type="button"
                            className="idz-sys-test"
                            onClick={(e) => { e.stopPropagation(); run.onResend(src); }}
                            disabled={!!run.resending}
                            title="Send this message through the pipeline again, exactly as it was received"
                        >
                            <RotateCcw size={13} /> {run.resending ? 'Replaying…' : 'Replay'}
                        </button>
                    )}
                </span>
                <span className="idz-sys-arrow" aria-hidden="true"><ArrowLeftRight size={14} /></span>
                </>}

                {isVendor ? (
                <div className={`idz-c3 idz-board-cell idz-proxy-id idz-vendor-id${runClass('proxy', src.id)}`} {...runOpen('proxy', src.id)}>
                    <span className="idz-board-heading idz-vendor-heading">
                        <span className="idz-vendor-kind">Vendor</span>
                        {src.vendor_system_image && (
                            <img className="idz-vendor-logo" src={src.vendor_system_image} alt=""
                                onError={(e) => { e.target.style.display = 'none'; }} />
                        )}
                        <span className="idz-proxy-name">{nodeTitle(src)}</span>
                    </span>
                    <span className="idz-vendor-note">
                        Calls the Universal API itself{src.vendor_system_name ? ` as a ${src.vendor_system_name} vendor` : ''} — its
                        routes are made as the vendor's own credential.
                    </span>
                    {!readOnly && (
                        <button
                            type="button"
                            className="idz-proxy-remove"
                            onClick={() => onRemove(src.id)}
                            title={`Remove ${nodeTitle(src)} and its routes`}
                        >
                            <Trash2 size={12} />
                            Remove
                        </button>
                    )}
                </div>
                ) : (
                <div className={`idz-c3 idz-board-cell idz-proxy-id${runClass('proxy', src.id)}`} {...runOpen('proxy', src.id)}>
                    {/* THE CHEVRON IS THE NAME'S OWN CONTROL. The key and the address are read once, when a
                        property is being set up, and then take a third of the column for the rest of the
                        integration's life — but they must not be hard to find, so the thing that folds them
                        is the line that names what they belong to. */}
                    {/* NO KEY, NO ADDRESS, ON A LOG. They are setup values — a secret and where to point
                        OPERA — and neither says anything about the message being read about. The name is
                        then plain text, so clicking the box opens that box's entries rather than a fold. */}
                    {run ? (
                        <span className="idz-board-heading">
                            <span className="idz-proxy-name">{src.adapter || nodeTitle(src)}</span>
                        </span>
                    ) : (
                        <button
                            type="button"
                            className="idz-proxy-fold"
                            aria-expanded={isOpen}
                            title={isOpen ? 'Hide the key and inbound URL' : 'Show the key and inbound URL'}
                            onClick={() => setExpanded(prev => {
                                const next = new Set(prev);
                                if (next.has(src.id)) next.delete(src.id); else next.add(src.id);
                                return next;
                            })}
                        >
                            {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            <span className="idz-board-heading">
                                <span className="idz-proxy-name">{src.adapter || nodeTitle(src)}</span>
                            </span>
                        </button>
                    )}
                    {/* THE ADAPTER'S ACTIONS, one row under its name that wraps as a whole — the credential it reaches
                        its system with, and (for a streaming source) its streaming client. Remove stays beside the name. */}
                    {(src.credential_id || hasAuth(src) || (!run && onEventDelivery && deliversEvents(src))
                        || (!run && onSourceConfig && Number(src.pipeline_settings || (system && system.pipeline_settings) || 0) > 0)
                        || (!run && onSourceRunNow && (src.run_now_button || (system && system.run_now_button)) === 'Y')) && (
                        <div className="idz-proxy-actions">
                        {/* THE SOURCE'S CREDENTIAL, ON THE SOURCE (the adapter) — what this proxy reaches its system with; on an
                            implementation, the one chosen for this source when it was added. */}
                        {/* CREDENTIALS — a system with AUTH settings signs in with a credential; on an implementation the
                            superadmin picks it here (and sees the chosen one), in place of the read-only Credential view. */}
                        {hasAuth(src) ? (
                            <button
                                type="button"
                                className={`idz-sys-test idz-proxy-stream${credState(src.credential_id)}`}
                                onClick={() => onSourceCredential(src)}
                                title={src.credential_id
                                    ? `The credential ${src.adapter || nodeTitle(src)} signs in to ${src.system_name || 'the system'} with — click to change it`
                                    : `Not set — click to choose the credential ${src.adapter || nodeTitle(src)} signs in to ${src.system_name || 'the system'} with`}
                            >
                                <KeyRound size={13} /> {src.credential_name ? `Credentials: ${src.credential_name}` : 'Credentials'}
                            </button>
                        ) : credButton(src.credential_id, 'Source credential', {
                            overrides: overridesOf(src.credential_overrides, src.hotelid_override),
                            overridesContext: `by ${src.adapter || nodeTitle(src)}`,
                            title: `Show the credential ${src.adapter || nodeTitle(src)} reaches ${src.system_name || 'the system'} with`,
                        })}
                        {/* EVENTS (V686) — ON THE ADAPTER, not the system: how this source's events reach the hub, polled
                            (the event poller) or streamed (a streaming client per hotel), never both — one button, one
                            screen, for every hotel implementing the template. Not on a log's board: that picture is one
                            message's past. */}
                        {!run && onEventDelivery && deliversEvents(src) && (
                            <button
                                type="button"
                                className="idz-sys-test idz-proxy-stream"
                                onClick={() => onEventDelivery(src)}
                                title={`How ${src.system_name || nodeTitle(src)} events reach the hub — polled or streamed — for every hotel implementing this template`}
                            >
                                <RefreshCw size={13} /> Events
                            </button>
                        )}
                        {/* CONFIG (V654) — the source system's PIPELINES settings, answered for this property (the Crawler's
                            url and interval). On the adapter with the other two, for the same reason. */}
                        {!run && onSourceConfig
                            && Number(src.pipeline_settings || (system && system.pipeline_settings) || 0) > 0 && (
                            <button
                                type="button"
                                className="idz-sys-test idz-proxy-stream"
                                onClick={() => onSourceConfig(src)}
                                title={`The settings ${src.system_name || nodeTitle(src)} runs with for this property`}
                            >
                                <SlidersHorizontal size={13} /> Config
                            </button>
                        )}
                        {/* RUN NOW (V658) — start this source now rather than on its schedule (the Crawler: one crawl of the
                            configured site). On the adapter with the others. */}
                        {!run && onSourceRunNow
                            && (src.run_now_button || (system && system.run_now_button)) === 'Y' && (
                            <button
                                type="button"
                                className="idz-sys-test idz-proxy-stream"
                                onClick={() => onSourceRunNow(src)}
                                title={`Run ${src.system_name || nodeTitle(src)} for this property now, rather than on its schedule`}
                            >
                                <Play size={13} /> Run now
                            </button>
                        )}
                        </div>
                    )}
                    {/* NAMED, NOT A BARE ×. This removes a whole source — its proxy, its key and every route
                        drawn from it — and it sat as a grey × in a corner, the same mark the module chips
                        use to drop one path. The two are not the same size of action and should not look
                        alike. */}
                    {!readOnly && (
                        <button
                            type="button"
                            className="idz-proxy-remove"
                            onClick={() => onRemove(src.id)}
                            title={`Remove ${nodeTitle(src)} and its routes`}
                        >
                            <Trash2 size={12} />
                            Remove
                        </button>
                    )}
                </div>
                )}

                {!run && !isVendor && isOpen && (
                    <div className="idz-c3 idz-board-cell">
                        {proxyAuth({ node: src, system })}
                        {system && system.authentication_scheme === 'API_KEY' && renderInboundUrl(src)}
                    </div>
                )}

                {/* ADD A ROUTE FROM THE MIDDLE. The button sits in the gap between the proxy and the hub,
                    where the row it creates will be drawn — the thing being added is the connection, and a
                    button labelled "Add route" inside the proxy column reads as something belonging to the
                    proxy. */}
                {!readOnly && onRoutesChange && (
                    <div className="idz-c4 idz-board-cell idz-add-cell">
                        <button
                            type="button"
                            className="idz-add-plus"
                            onClick={() => { setEditingIndex(null); setRouteModalNodeId(src.id); }}
                            title={`Add a route from ${isVendor ? nodeTitle(src) : (src.adapter || nodeTitle(src))}`}
                        >
                            <Plus size={16} />
                        </button>
                    </div>
                )}

                {mine.map(routeRow)}

                {mine.length === 0 && (
                    <div className="idz-c3 idz-board-cell">
                        <p className="idz-empty">
                            No routes yet. Use <strong>+</strong> to send something through the Universal API.
                        </p>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
        {sources.length > 0 && renderHeadings()}
        {sources.map(renderBoard)}

        {sources.length === 0 && (
            <p className="idz-empty idz-empty-page">
                {/* THREE DIFFERENT FACTS, three different sentences. "Nothing is marked as a source" sends
                    somebody to the marketplace screen; "the list has not loaded" sends them nowhere, and
                    saying the first when the second is true wastes the trip. */}
                {readOnly
                    ? 'Nothing sends into this integration yet.'
                    : (systems || []).length === 0
                        ? 'The list of systems has not loaded.'
                        : sourceSystemCount === 0
                            ? 'No system is marked as an integration source yet. Mark one on its marketplace '
                              + 'record, ESB Integration tab.'
                            : 'Nothing sends into this integration yet. Add the system that sends, and its '
                              + 'routes are drawn across from it.'}
            </p>
        )}

        {/* Only when there is no board to hang it on. With one, the button lives beside the Sources heading
            — where what it adds actually appears. */}
        {!readOnly && sources.length === 0 && (
            <div className="idz-page-actions">
                {renderAddButtons()}
            </div>
        )}

        {targetPick && (
            // THE SAME PICKER AS EVERY PRODUCT — the shared selector with its overrides asked in the screen, opened
            // from the target cell's own button rather than a chip.
            <CredentialSelector
                screenOnly
                overridesInPicker
                open
                onOpenChange={(v) => { if (!v) setTargetPick(null); }}
                marketplaceId={targetPick.marketplaceId}
                hotelId={hotelId}
                accountId={accountId}
                partnerId={partnerId}
                canEdit={isSuperAdmin}
                title={`${targetPick.systemName} credential`}
                subtitle="What this property signs in to the target with."
                value={targetPick.selected}
                overrides={targetPick.values}
                overridesTitle="For this property"
                onChange={(cred, values) => { if (cred) onPickTargetCredential(targetPick.route, cred, values || {}); }}
            />
        )}
        {credView && (
            <CredentialModalById
                credentialId={credView.credentialId}
                overrides={credView.overrides}
                overridesContext={credView.overridesContext}
                onClose={() => setCredView(null)}
            />
        )}
        <AddRouteModal
            isOpen={!!routeModalNode}
            onClose={() => { setRouteModalNodeId(null); setEditingIndex(null); }}
            modules={routeModalSystem ? routeModalSystem.modules : []}
            existing={routes.filter(r => routeModalNode && String(r.from_node_id) === String(routeModalNode.id))}
            route={editingIndex === null ? null : routes[editingIndex]}
            sourceAdapter={routeModalNode ? (routeModalNode.adapter || routeModalNode.vendor_name || null) : null}
            vendor={routeModalNode && routeModalNode.role === 'VENDOR' ? nodeTitle(routeModalNode) : null}
            // THE BACKENDS A ROUTE MAY BE DESIGNED FOR (V510) — the palette, filtered by uapi_enabled in
            // the dialog. The whole list goes in so the flag is read off the marketplace record rather
            // than a second copy of the rule living here.
            systems={systems}
            onAdd={({ moduleIds, targetMarketplaceId, name }) => {
                if (!routeModalNode || !onRoutesChange) return;
                // The source's modules and the designed-for backend's (AddRouteModal offers both — a module is the
                // backend's root, which the source's system may not name).
                const target = targetMarketplaceId ? systemById.get(String(targetMarketplaceId)) : null;
                const all = [...((routeModalSystem && routeModalSystem.modules) || []), ...((target && target.modules) || [])];
                const modsFor = (ids) => ids.map(id => {
                    const m = all.find(x => String(x.uapi_route_id) === String(id)) || {};
                    return { uapi_route_id: Number(id), name: m.name, uapi_route_path: m.uapi_route_path };
                });
                // EVERY + MAKES A NEW ROUTE (V481). It used to merge into an existing route with the same
                // proxy and credential, because the table was unique on that pair — so a second press piled
                // its modules onto the first row instead of drawing the row that was asked for. Custom code
                // attaches to a ROUTE, so "/rsv with the reservation mapper" and "/act with nothing" have to
                // be two routes even though both carry the same shape.
                //
                // NO CREDENTIAL AND NO BINDING ARE SET HERE any more. A template route says what it
                // carries, what KIND of backend it is for, and what it is called; who it travels as and
                // WHICH backend it lands on are filled in per property by an implementation (V508/V510).
                // An existing route KEEPS whatever it already has — editing the modules of a route drawn
                // before this change must not silently clear the credential the pipeline is currently
                // running on.
                const carried = {
                    name: name || null,
                    target_marketplace_id: targetMarketplaceId,
                    modules: modsFor(moduleIds),
                };
                // EDITING REPLACES THE ROW IT WAS OPENED ON, keeping its id — and with it the custom code
                // attached to that route, which a delete-and-recreate would drop.
                if (editingIndex !== null) {
                    onRoutesChange(routes.map((r, j) => j === editingIndex ? { ...r, ...carried } : r));
                    setEditingIndex(null);
                    return;
                }
                // ACTIVE FROM THE START. A route drawn is a route meant to carry traffic; it still sends nothing until an
                // implementation is active at a property, which is the deliberate switch. Starting it inactive only
                // left a second toggle to find before anything flowed. Its own toggle still turns it off.
                onRoutesChange([...routes, {
                    from_node_id: routeModalNode.id,
                    to_node_id: null,
                    ...carried,
                    active: 'Y',
                }]);
            }}
        />
        </>
    );
};

export default IntegrationDesigner;
